import { cn } from '@/lib/utils';
import { AlertCircle } from 'lucide-react';
import { useContext, useMemo, useState } from 'react';
import Lottie from 'lottie-react';

import {
  ChainlitContext,
  useChatSession,
  useConfig
} from '@chainlit/react-client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

// Import the Lottie animation
import avatarIconAnimation from '@/assets/avatar-icon-animation.json';

interface Props {
  author?: string;
  hide?: boolean;
  isError?: boolean;
  isLoading?: boolean;
}

const MessageAvatar = ({ author, hide, isError, isLoading }: Props) => {
  const apiClient = useContext(ChainlitContext);
  const { chatProfile } = useChatSession();
  const { config } = useConfig();
  const [lottieError, setLottieError] = useState(false);

  const selectedChatProfile = useMemo(() => {
    return config?.chatProfiles.find((profile) => profile.name === chatProfile);
  }, [config, chatProfile]);

  const avatarUrl = useMemo(() => {
    if (config?.ui?.default_avatar_file_url) return config?.ui?.default_avatar_file_url;
    const isAssistant = !author || author === config?.ui.name;
    if (isAssistant && selectedChatProfile?.icon) {
      return selectedChatProfile.icon;
    }
    return apiClient?.buildEndpoint(`/avatars/${author || 'default'}`);
  }, [apiClient, selectedChatProfile, config, author]);

  if (isError) {
    return (
      <AlertCircle className="h-5 w-5 fill-destructive mt-[5px] text-destructive-foreground" />
    );
  }

  // Show Lottie animation when loading
  if (isLoading && !lottieError) {
    return (
      <span className={cn('inline-block', hide && 'invisible')}>
        <div className="flex items-center justify-center w-5 h-5 mt-[3px] overflow-hidden bg-transparent">
          <Lottie
            animationData={avatarIconAnimation}
            loop={true}
            autoplay={true}
            renderer="svg"
            style={{ 
              width: '100%', 
              height: '100%',
              minWidth: '20px',
              minHeight: '20px',
              transform: 'scale(1)',
              transformOrigin: 'center',
              backgroundColor: 'transparent'
            }}
            rendererSettings={{
              preserveAspectRatio: 'xMidYMid meet',
              progressiveLoad: false,
              hideOnTransparent: false
            }}
            onError={() => setLottieError(true)}
            onComplete={() => {
              // Force re-render on complete to prevent flickering
            }}
          />
        </div>
      </span>
    );
  }

  // Fallback for loading state if Lottie fails
  if (isLoading && lottieError) {
    return (
      <span className={cn('inline-block', hide && 'invisible')}>
        <div className="h-5 w-5 mt-[3px]">
          <Skeleton className="h-full w-full rounded-full animate-pulse" />
        </div>
      </span>
    );
  }

  return (
    <span className={cn('inline-block', hide && 'invisible')}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Avatar className="h-5 w-5 mt-[3px]">
              <AvatarImage
                src={avatarUrl}
                alt={`Avatar for ${author || 'default'}`}
                className="bg-transparent"
              />
              <AvatarFallback className="bg-transparent">
                <Skeleton className="h-full w-full rounded-full" />
              </AvatarFallback>
            </Avatar>
          </TooltipTrigger>
          <TooltipContent>
            <p>{author}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
};

export { MessageAvatar };
